import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { 
  Search, 
  Plus, 
  Edit, 
  Trash2, 
  Eye, 
  Power,
  RefreshCw,
  Package,
  DollarSign,
  Globe,
  Tag,
  AlertCircle,
  XCircle
} from 'lucide-react';
import { ProductService } from '../../services/productService';
import { CategoryService } from '../../services/categoryService';
import LoadingSpinner from '../common/LoadingSpinner';
import { useNotification } from '../common/Notification';
import { AlertContext } from '../../contexts/AlertContext';

const ProductManagement = () => {
  const { addNotification } = useNotification();
  const { addAlert } = useContext(AlertContext);
  const location = useLocation();
  const requestedSection = useMemo(() => {
    if (location.pathname === '/admin/topup/services') {
      return CategoryService.CATEGORY_SECTIONS.TOPUP;
    }

    if (location.pathname === '/admin/products') {
      return CategoryService.CATEGORY_SECTIONS.CARDS;
    }

    const params = new URLSearchParams(location.search);
    const section = params.get('section');
    if (section === CategoryService.CATEGORY_SECTIONS.TOPUP) {
      return CategoryService.CATEGORY_SECTIONS.TOPUP;
    }

    if (section === CategoryService.CATEGORY_SECTIONS.CARDS) {
      return CategoryService.CATEGORY_SECTIONS.CARDS;
    }

    return 'all';
  }, [location.pathname, location.search]);
  const isSectionLocked = requestedSection !== 'all';
  
  // États pour les produits
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sectionFilter, setSectionFilter] = useState(requestedSection);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [showProductModal, setShowProductModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [productToDelete, setProductToDelete] = useState(null);
  const [actionLoading, setActionLoading] = useState({});

  const loadProducts = useCallback(async () => {
    try {
      setLoading(true);
      const productsData = await ProductService.getAllProducts();
      setProducts(productsData);
    } catch (error) {
      console.error('Erreur lors du chargement des produits:', error);
      addAlert('Erreur lors du chargement des produits', 'error');
    } finally {
      setLoading(false);
    }
  }, [addAlert]);

  const loadCategories = useCallback(async () => {
    try {
      const categoriesData = await CategoryService.getAllCategories();
      setCategories(categoriesData);
    } catch (error) {
      console.error('Erreur lors du chargement des catégories pour les produits:', error);
    }
  }, []);

  useEffect(() => {
    loadProducts();
    loadCategories();
  }, [loadCategories, loadProducts]);

  useEffect(() => {
    setSectionFilter(requestedSection);
  }, [requestedSection]);

  const resolveTopupCategory = useCallback((product = {}) => {
    if (ProductService.resolveProductSection(product, categories) !== CategoryService.CATEGORY_SECTIONS.TOPUP) {
      return null;
    }

    const categoryById = categories.find((category) => (
      category.id === product.categoryId && CategoryService.isTopupParentCategory(category)
    )) || null;
    const subcategoryById = categories.find((category) => (
      category.id === product.subcategoryId && CategoryService.isTopupSubcategory(category)
    )) || null;
    const normalizedProductCategory = CategoryService.normalizeCategory(product.category);
    const categoryByName = categories.find((category) => (
      CategoryService.isTopupParentCategory(category) &&
      CategoryService.normalizeCategory(category.name) === normalizedProductCategory
    )) || null;

    if (categoryById) {
      return categoryById;
    }

    if (subcategoryById) {
      return categories.find((category) => category.id === subcategoryById.parentCategoryId) || null;
    }

    return categoryByName;
  }, [categories]);

  const resolveProductSection = useCallback(
    (product) => ProductService.resolveProductSection(product, categories),
    [categories]
  );

  const resolveProductGrouping = useCallback((product) => {
    const section = resolveProductSection(product);
    const categoryById = categories.find((category) => category.id === product.categoryId) || null;
    const normalizedProductCategory = CategoryService.normalizeCategory(product.category);
    const categoryByName = categories.find((category) => (
      CategoryService.normalizeCategory(category.name) === normalizedProductCategory
    )) || null;

    if (section === CategoryService.CATEGORY_SECTIONS.TOPUP) {
      const parentCategory = resolveTopupCategory(product);
      const subcategoryById = categories.find((category) => (
        category.id === product.subcategoryId && CategoryService.isTopupSubcategory(category)
      )) || null;
      const normalizedSubcategoryName = CategoryService.normalizeCategory(product.subcategoryName);
      const subcategoryByName = normalizedSubcategoryName
        ? categories.find((category) => (
            CategoryService.isTopupSubcategory(category) &&
            CategoryService.normalizeCategory(category.name) === normalizedSubcategoryName
          )) || null
        : null;
      const subcategory = subcategoryById || subcategoryByName;

      if (parentCategory && subcategory) {
        return {
          key: `topup:${parentCategory.id}:${subcategory.id}`,
          title: subcategory.name,
          subtitle: `Categorie parent: ${parentCategory.name}`,
          categoryLabel: parentCategory.name,
          subcategoryLabel: subcategory.name,
          section
        };
      }

      if (parentCategory) {
        return {
          key: `topup:${parentCategory.id}:uncategorized`,
          title: parentCategory.name,
          subtitle: 'Services sans sous-categorie',
          categoryLabel: parentCategory.name,
          subcategoryLabel: '',
          section
        };
      }

      return {
        key: 'topup:uncategorized',
        title: 'Topup non classes',
        subtitle: 'Services a rattacher a une categorie Topup',
        categoryLabel: product.category || 'Topup',
        subcategoryLabel: String(product.subcategoryName || '').trim(),
        section
      };
    }

    const cardCategory = categoryById || categoryByName;

    return {
      key: `cards:${cardCategory?.id || normalizedProductCategory || 'uncategorized'}`,
      title: cardCategory?.name || product.category || 'Sans categorie',
      subtitle: 'Produits cartes',
      categoryLabel: cardCategory?.name || product.category || 'Sans categorie',
      subcategoryLabel: '',
      section
    };
  }, [categories, resolveProductSection, resolveTopupCategory]);

  const handleDeleteProduct = async () => {
    if (!productToDelete) return;
    
    setActionLoading(prev => ({ ...prev, delete: true }));
    
    try {
      await ProductService.deleteProduct(productToDelete.id);
      addNotification('success', 'Produit supprimé', 'Produit supprimé avec succès');
      setShowDeleteModal(false);
      setProductToDelete(null);
      await loadProducts();
    } catch (error) {
      console.error('Erreur lors de la suppression:', error);
      addNotification('error', 'Erreur de suppression', error.message || 'Erreur lors de la suppression');
    } finally {
      setActionLoading(prev => ({ ...prev, delete: false }));
    }
  };

  const handleToggleProductStatus = async (product) => {
    if (!product?.id) return;

    const nextStatus = product.status === 'active' ? 'inactive' : 'active';
    setActionLoading((current) => ({ ...current, [`status_${product.id}`]: true }));

    try {
      await ProductService.updateProduct(product.id, { status: nextStatus });

      setProducts((current) => current.map((entry) => (
        entry.id === product.id
          ? { ...entry, status: nextStatus }
          : entry
      )));

      setSelectedProduct((current) => (
        current?.id === product.id
          ? { ...current, status: nextStatus }
          : current
      ));

      addNotification(
        'success',
        nextStatus === 'active' ? 'Produit active' : 'Produit desactive',
        `${product.name} est maintenant ${nextStatus === 'active' ? 'actif' : 'inactif'}`
      );
    } catch (error) {
      console.error('Erreur lors du changement de statut produit:', error);
      addNotification(
        'error',
        'Impossible de changer le statut',
        error.message || 'Erreur lors de la mise a jour du produit'
      );
    } finally {
      setActionLoading((current) => ({ ...current, [`status_${product.id}`]: false }));
    }
  };

  const handleSyncCardStocks = async () => {
    setActionLoading((current) => ({ ...current, syncStock: true }));

    try {
      const result = await ProductService.syncAllCardProductStocks();
      addNotification(
        'success',
        'Stocks synchronises',
        `${result.changed} produit(s) carte(s) mis a jour sur ${result.total}`
      );
      await loadProducts();
    } catch (error) {
      console.error('Erreur lors de la synchronisation des stocks cartes:', error);
      addNotification('error', 'Synchronisation impossible', error.message || 'Impossible de synchroniser les stocks cartes');
    } finally {
      setActionLoading((current) => ({ ...current, syncStock: false }));
    }
  };

  const filteredProducts = useMemo(() => products.filter(product => {
    const normalizedSearch = searchTerm.toLowerCase();
    const grouping = resolveProductGrouping(product);
    const matchesSearch =
      product.name.toLowerCase().includes(normalizedSearch) ||
      String(product.category || '').toLowerCase().includes(normalizedSearch) ||
      String(product.subcategoryName || '').toLowerCase().includes(normalizedSearch) ||
      String(grouping.title || '').toLowerCase().includes(normalizedSearch);
    const matchesStatus = statusFilter === 'all' || product.status === statusFilter;
    const matchesSection = sectionFilter === 'all' || resolveProductSection(product) === sectionFilter;
    return matchesSearch && matchesStatus && matchesSection;
  }), [products, resolveProductGrouping, resolveProductSection, searchTerm, sectionFilter, statusFilter]);

  const groupedProducts = useMemo(() => {
    const groupsMap = new Map();

    filteredProducts.forEach((product) => {
      const grouping = resolveProductGrouping(product);
      const currentGroup = groupsMap.get(grouping.key) || {
        ...grouping,
        products: []
      };

      currentGroup.products.push(product);
      groupsMap.set(grouping.key, currentGroup);
    });

    return Array.from(groupsMap.values())
      .map((group) => ({
        ...group,
        products: group.products.sort((a, b) => a.name.localeCompare(b.name))
      }))
      .sort((a, b) => {
        if (a.section !== b.section) {
          return a.section.localeCompare(b.section);
        }

        return a.title.localeCompare(b.title);
      });
  }, [filteredProducts, resolveProductGrouping]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* En-tête */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">
                {sectionFilter === CategoryService.CATEGORY_SECTIONS.TOPUP ? 'Services Topup' : 'Gestion des produits'}
              </h1>
              <p className="text-gray-600 mt-1">
                {sectionFilter === CategoryService.CATEGORY_SECTIONS.TOPUP
                  ? 'Cree, relie et gere les services Topup existants sans doublons.'
                  : 'Gerez uniquement les produits cartes et leurs offres.'}
              </p>
            </div>
            
            <div className="flex items-center gap-3">
              {sectionFilter === CategoryService.CATEGORY_SECTIONS.TOPUP && (
                <Link
                  to="/admin/topup/templates"
                  className="inline-flex items-center rounded-lg border border-gray-300 px-4 py-2 text-gray-700 transition-colors hover:bg-gray-50"
                >
                  Templates Topup
                </Link>
              )}
              {sectionFilter !== CategoryService.CATEGORY_SECTIONS.TOPUP && (
                <button
                  type="button"
                  onClick={handleSyncCardStocks}
                  disabled={actionLoading.syncStock}
                  className="inline-flex items-center rounded-lg border border-gray-300 px-4 py-2 text-gray-700 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  <RefreshCw className={`mr-2 h-4 w-4 ${actionLoading.syncStock ? 'animate-spin' : ''}`} />
                  Synchroniser les stocks
                </button>
              )}
              <Link
                to={sectionFilter === CategoryService.CATEGORY_SECTIONS.TOPUP ? '/admin/topup/services/new' : '/admin/products/new'}
                className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
              >
                <Plus className="w-4 h-4 mr-2" />
                {sectionFilter === CategoryService.CATEGORY_SECTIONS.TOPUP ? 'Creer un service Topup' : 'Creer un produit'}
              </Link>
            </div>
          </div>
        </div>

        {/* Filtres de recherche */}
        <div className="mb-6 flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Rechercher un produit, une categorie ou une sous-categorie..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>
          </div>
          
          {!isSectionLocked && (
            <div className="sm:w-48">
              <select
                value={sectionFilter}
                onChange={(e) => setSectionFilter(e.target.value)}
                className="mb-4 w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent sm:mb-0"
              >
                <option value="all">Toutes les sections</option>
                <option value={CategoryService.CATEGORY_SECTIONS.CARDS}>Cartes</option>
                <option value={CategoryService.CATEGORY_SECTIONS.TOPUP}>Topup</option>
              </select>
            </div>
          )}

          <div className="sm:w-48">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            >
              <option value="all">Tous les statuts</option>
              <option value="active">Actif</option>
              <option value="inactive">Inactif</option>
            </select>
          </div>
        </div>

        {/* Liste des produits */}
        <div className="space-y-8">
          {groupedProducts.length > 0 ? (
            groupedProducts.map((group) => (
              <section key={group.key} className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
                <div className="mb-5 flex flex-col gap-3 border-b border-gray-100 pb-4 sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    <div className="flex items-center gap-2">
                      <h2 className="text-lg font-semibold text-gray-900">{group.title}</h2>
                      <span className={`rounded-full px-2.5 py-1 text-xs font-medium ${
                        group.section === CategoryService.CATEGORY_SECTIONS.TOPUP
                          ? 'bg-blue-100 text-blue-700'
                          : 'bg-purple-100 text-purple-700'
                      }`}>
                        {group.section === CategoryService.CATEGORY_SECTIONS.TOPUP ? 'Topup' : 'Cartes'}
                      </span>
                    </div>
                    <p className="mt-1 text-sm text-gray-500">{group.subtitle}</p>
                  </div>
                  <div className="inline-flex items-center rounded-full bg-gray-100 px-3 py-1 text-sm font-medium text-gray-600">
                    {group.products.length} produit{group.products.length > 1 ? 's' : ''}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                  {group.products.map((product) => (
                    <div key={product.id} className="overflow-hidden rounded-xl border border-gray-200 bg-white">
                      <div className="aspect-w-16 aspect-h-9">
                        <img
                          src={product.imageUrl || '/api/placeholder/400/225'}
                          alt={product.name}
                          className="h-48 w-full object-cover"
                        />
                      </div>
                      <div className="p-4">
                        <div className="mb-2 flex items-center justify-between gap-3">
                          <h3 className="text-lg font-semibold text-gray-900">{product.name}</h3>
                          <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                            product.status === 'active'
                              ? 'bg-green-100 text-green-800'
                              : 'bg-red-100 text-red-800'
                          }`}>
                            {product.status === 'active' ? 'Actif' : 'Inactif'}
                          </span>
                        </div>
                        
                        <div className="mb-2 flex items-center text-sm text-gray-600">
                          <Tag className="mr-1 h-4 w-4" />
                          <span>{product.category || group.categoryLabel}</span>
                        </div>

                        {product.subcategoryName ? (
                          <div className="mb-2 flex items-center text-sm text-gray-500">
                            <Tag className="mr-1 h-4 w-4" />
                            <span>Sous-categorie: {product.subcategoryName}</span>
                          </div>
                        ) : null}

                        {product.topupTemplateName && (
                          <div className="mb-2 rounded-lg bg-blue-50 px-3 py-2 text-xs font-medium text-blue-700">
                            Template: {product.topupTemplateName}
                          </div>
                        )}
                        
                        {product.games && product.games.length > 0 && (
                          <div className="mb-3 flex items-center text-sm text-gray-600">
                            <Globe className="mr-1 h-4 w-4" />
                            <span>{product.games.join(', ')}</span>
                          </div>
                        )}
                        
                        <div className="flex items-center justify-between">
                          <div className="flex items-center font-semibold text-gray-900">
                            <DollarSign className="mr-1 h-4 w-4" />
                            <span>{product.price} DA</span>
                          </div>
                          
                          <div className="flex space-x-2">
                            <button
                              onClick={() => handleToggleProductStatus(product)}
                              disabled={actionLoading[`status_${product.id}`]}
                              className={`p-2 transition-colors disabled:cursor-not-allowed disabled:opacity-60 ${
                                product.status === 'active'
                                  ? 'text-gray-400 hover:text-amber-600'
                                  : 'text-gray-400 hover:text-emerald-600'
                              }`}
                              title={product.status === 'active' ? 'Desactiver le produit' : 'Activer le produit'}
                            >
                              <Power className={`h-4 w-4 ${actionLoading[`status_${product.id}`] ? 'animate-pulse' : ''}`} />
                            </button>
                            <button
                              onClick={() => {
                                setSelectedProduct(product);
                                setShowProductModal(true);
                              }}
                              className="p-2 text-gray-400 hover:text-primary-600 transition-colors"
                            >
                              <Eye className="h-4 w-4" />
                            </button>
                            <Link
                              to={`/admin/products/${product.id}/edit${resolveProductSection(product) === CategoryService.CATEGORY_SECTIONS.TOPUP ? '?section=topup' : ''}`}
                              className="p-2 text-gray-400 hover:text-primary-600 transition-colors"
                            >
                              <Edit className="h-4 w-4" />
                            </Link>
                            <button
                              onClick={() => {
                                setProductToDelete(product);
                                setShowDeleteModal(true);
                              }}
                              className="p-2 text-gray-400 hover:text-red-600 transition-colors"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            ))
          ) : (
            <div className="col-span-full">
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
                <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
                  <Package className="w-8 h-8 text-gray-400" />
                </div>
                <p className="text-gray-500 mb-2">Aucun produit trouvé</p>
                <p className="text-sm text-gray-400 mb-4">
                  {searchTerm || statusFilter !== 'all'
                    ? 'Essayez de modifier vos critères de recherche'
                    : sectionFilter === CategoryService.CATEGORY_SECTIONS.TOPUP
                      ? 'Commencez par creer votre premier service Topup'
                      : 'Commencez par creer votre premier produit carte'
                  }
                </p>
                {!searchTerm && statusFilter === 'all' && (
                  <Link
                    to={sectionFilter === CategoryService.CATEGORY_SECTIONS.TOPUP ? '/admin/topup/services/new' : '/admin/products/new'}
                    className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    {sectionFilter === CategoryService.CATEGORY_SECTIONS.TOPUP ? 'Creer un service Topup' : 'Creer un produit carte'}
                  </Link>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Modal de détails du produit */}
      {showProductModal && selectedProduct && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">
                  {selectedProduct.name}
                </h3>
                <button
                  onClick={() => setShowProductModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XCircle className="w-6 h-6" />
                </button>
              </div>
            </div>
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <img
                    src={selectedProduct.imageUrl || '/api/placeholder/400/300'}
                    alt={selectedProduct.name}
                    className="w-full h-64 object-cover rounded-lg"
                  />
                </div>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Prix</label>
                    <p className="text-2xl font-bold text-gray-900">{selectedProduct.price} DA</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Catégorie</label>
                    <p className="text-gray-900">{selectedProduct.category}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Section</label>
                    <p className="text-gray-900">
                      {resolveProductSection(selectedProduct) === CategoryService.CATEGORY_SECTIONS.TOPUP ? 'Topup' : 'Cartes'}
                    </p>
                  </div>
                  {selectedProduct.topupTemplateName && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Template Topup</label>
                      <p className="text-gray-900">{selectedProduct.topupTemplateName}</p>
                    </div>
                  )}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Jeux</label>
                    <p className="text-gray-900">{selectedProduct.games?.join(', ') || 'Aucun'}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Statut</label>
                    <div className="flex items-center gap-3">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                        selectedProduct.status === 'active'
                          ? 'bg-green-100 text-green-800'
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {selectedProduct.status === 'active' ? 'Actif' : 'Inactif'}
                      </span>
                      <button
                        type="button"
                        onClick={() => handleToggleProductStatus(selectedProduct)}
                        disabled={actionLoading[`status_${selectedProduct.id}`]}
                        className={`inline-flex items-center rounded-lg px-3 py-1.5 text-xs font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-60 ${
                          selectedProduct.status === 'active'
                            ? 'bg-amber-100 text-amber-700 hover:bg-amber-200'
                            : 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200'
                        }`}
                      >
                        <Power className="mr-2 h-3.5 w-3.5" />
                        {selectedProduct.status === 'active' ? 'Desactiver' : 'Activer'}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
              
              {selectedProduct.description && (
                <div className="mt-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                  <p className="text-gray-900">{selectedProduct.description}</p>
                </div>
              )}
              
              {selectedProduct.offers && selectedProduct.offers.length > 0 && (
                <div className="mt-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Offres disponibles</label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {selectedProduct.offers.map((offer, index) => (
                      <div key={index} className="border border-gray-200 rounded-lg p-4">
                        <div className="flex items-center justify-between mb-2">
                          <span className="font-medium">{offer.amount} {offer.currency}</span>
                          <span className="text-primary-600 font-semibold">{offer.price} DA</span>
                        </div>
                        {offer.bonus && (
                          <p className="text-sm text-green-600">Bonus: {offer.bonus}</p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Modal de confirmation de suppression */}
      {showDeleteModal && productToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center mb-4">
                <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center mr-3">
                  <AlertCircle className="w-6 h-6 text-red-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">Confirmer la suppression</h3>
              </div>
              <p className="text-gray-600 mb-6">
                Êtes-vous sûr de vouloir supprimer le produit "{productToDelete.name}" ? 
                Cette action est irréversible.
              </p>
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => {
                    setShowDeleteModal(false);
                    setProductToDelete(null);
                  }}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Annuler
                </button>
                <button
                  onClick={handleDeleteProduct}
                  disabled={actionLoading.delete}
                  className="px-4 py-2 bg-danger-600 text-white rounded-lg hover:bg-danger-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center"
                >
                  {actionLoading.delete ? (
                    <>
                      <LoadingSpinner size="sm" className="mr-2" />
                      Suppression...
                    </>
                  ) : (
                    'Supprimer'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductManagement;
